import type { DrawToolId, MediaOverlays, OverlayFontId, OverlayStroke } from '../types';

export const OVERLAY_FONTS: { id: OverlayFontId; label: string; family: string; weight: number }[] = [
  { id: 'classic', label: 'Classic', family: "'Inter', sans-serif", weight: 900 },
  { id: 'serif', label: 'Serif', family: "'Playfair Display', serif", weight: 900 },
  { id: 'mono', label: 'Mono', family: "'Space Mono', monospace", weight: 700 },
  { id: 'poster', label: 'Poster', family: "'Bebas Neue', sans-serif", weight: 400 },
  { id: 'script', label: 'Script', family: "'Pacifico', cursive", weight: 400 },
  { id: 'marker', label: 'Marker', family: "'Permanent Marker', cursive", weight: 400 },
  { id: 'bold', label: 'Impact', family: "'Anton', sans-serif", weight: 400 },
];

export const fontById = (id: OverlayFontId) => OVERLAY_FONTS.find((f) => f.id === id) || OVERLAY_FONTS[0];

export const OVERLAY_COLORS = [
  '#ffffff', '#000000', '#ef4444', '#f97316', '#facc15', '#a3e635', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#9ca3af',
];

// Pen/brush/highlighter look. `mult` scales the chosen thickness, `opacity` is the stroke alpha.
export const TOOL_STYLE: Record<DrawToolId, { mult: number; opacity: number; cap: 'round' | 'square' }> = {
  pen: { mult: 1, opacity: 1, cap: 'round' },
  brush: { mult: 2, opacity: 0.85, cap: 'round' },
  highlighter: { mult: 3.2, opacity: 0.38, cap: 'square' },
};

// Thickness presets, as a fraction of the media width.
export const THICKNESS_PRESETS = [0.004, 0.008, 0.014, 0.024];

export const MAX_POINTS = 9000; // keeps the saved document comfortably small

export const emptyOverlays = (aspect: number): MediaOverlays => ({ aspect: aspect > 0 ? aspect : 1, texts: [], strokes: [] });

export const hasOverlayContent = (o?: MediaOverlays | null): boolean => !!o && (o.texts.some((t) => t.text.trim()) || o.strokes.length > 0);

export const countPoints = (o: MediaOverlays) => o.strokes.reduce((n, s) => n + s.points.length / 2, 0);

export const round4 = (n: number) => Math.round(n * 10000) / 10000;

// Smooth SVG path through the points (quadratic curves through midpoints). `h` is the viewBox height for the y scale.
export function strokePath(points: number[], h: number): string {
  const n = points.length / 2;
  if (n === 0) return '';
  const X = (i: number) => points[i * 2] * 1000;
  const Y = (i: number) => points[i * 2 + 1] * h;
  if (n === 1) return `M${X(0)} ${Y(0)} L${X(0) + 0.01} ${Y(0)}`;
  let d = `M${X(0)} ${Y(0)}`;
  for (let i = 1; i < n - 1; i++) {
    const mx = (X(i) + X(i + 1)) / 2;
    const my = (Y(i) + Y(i + 1)) / 2;
    d += ` Q${X(i)} ${Y(i)} ${mx} ${my}`;
  }
  d += ` L${X(n - 1)} ${Y(n - 1)}`;
  return d;
}

// Eraser: removes the parts of strokes within `radius` (normalised to width) of the point, splitting strokes in two
// where the eraser crossed them. `aspect` converts y distances so the eraser is a true circle.
export function eraseAt(strokes: OverlayStroke[], x: number, y: number, radius: number, aspect: number): OverlayStroke[] {
  const out: OverlayStroke[] = [];
  const hit = (px: number, py: number) => {
    const dx = px - x;
    const dy = (py - y) / aspect; // y is fraction of height; height = width/aspect
    return Math.hypot(dx, dy) <= radius;
  };
  for (const stroke of strokes) {
    const pts = stroke.points;
    const n = pts.length / 2;
    let touched = false;
    for (let i = 0; i < n; i++) { if (hit(pts[i * 2], pts[i * 2 + 1])) { touched = true; break; } }
    if (!touched) { out.push(stroke); continue; }
    let cur: number[] = [];
    let part = 0;
    const flush = () => {
      if (cur.length >= 4) out.push({ ...stroke, id: `${stroke.id}_${part++}`, points: cur });
      cur = [];
    };
    for (let i = 0; i < n; i++) {
      if (hit(pts[i * 2], pts[i * 2 + 1])) flush();
      else cur.push(pts[i * 2], pts[i * 2 + 1]);
    }
    flush();
  }
  return out;
}

export const newId = () => Math.random().toString(36).slice(2, 9);
